/**
 * Schedule Permissions Utility
 * 
 * This module provides centralized permission management for the scheduling components.
 * It defines which fields each role can view and edit, replacing the scattered permission
 * checks throughout the codebase.
 */

/**
 * Field permissions configuration
 * Defines which fields each role can edit
 */
const FIELD_PERMISSIONS = {
  // Admin role - unrestricted access to all fields
  Admin: {
    canEditAnyField: true,
    editableFields: 'all'
  },
  
  // Scheduling role - minimal restrictions, can edit most fields
  Scheduling: {
    canEditAnyField: true,
    editableFields: 'all',
    // Exception: Cannot edit sensitive fields unless job has scheduleOnly = "Yes"
    restrictedFields: ['JobNumber', 'ModelNumber', 'ProjectName'],
    restrictedFieldsCondition: (job) => job.scheduleOnly !== "Yes" && job.ScheduleOnly !== "Yes"
  },
  
  // EngineerManager role - can edit engineering-related fields only
  EngineerManager: {
    canEditAnyField: false,
    editableFields: [
      'MechanicalEngineer',
      'ElectricalEngineer', 
      'ControlsEngineer',
      'MechanicalDue',
      'MechanicalDone',
      'MechanicalDuration',
      'ElectricalDue',
      'ElectricalDone',
      'ElectricalDuration',
      'ControlsDue',
      'ControlsDone',
      'ControlsDuration',
      'MoveRequestDateInitiated',
      'MoveRequestRequestedDate',
      'MoveRequestReasonForMove',
      'MoveUpOut',
      'MoveRequestDateMovedUpOut'
    ]
  },
  
  // Accounting role - can edit financial fields only
  Accounting: {
    canEditAnyField: false,
    editableFields: [
      'DollarValue',
      'PaymentTerms'
    ]
  },
  
  // Everyone else - read only
  ReadOnly: {
    canEditAnyField: false,
    editableFields: []
  }
};

/**
 * Normalize roles input to ensure compatibility with both array and object formats
 * 
 * @param {Array|Object} roles - User roles in array or object format
 * @returns {Object} - Normalized roles object
 */
const normalizeRoles = (roles) => {
  // If it's already an object, return as-is
  if (roles && typeof roles === 'object' && !Array.isArray(roles)) {
    return roles;
  }
  
  // If it's an array, convert to object format
  if (Array.isArray(roles)) {
    const roleObj = {};
    roles.forEach(role => {
      if (typeof role === 'string') {
        roleObj[role] = true;
      }
    });
    return roleObj;
  }
  
  // Return empty object if roles is null/undefined
  return {};
};

/**
 * Get the effective role for permission checking
 * Roles are checked in priority order (Admin > Scheduling > EngineerManager > Accounting > ReadOnly)
 * 
 * @param {Array|Object} roles - User roles in array or object format
 * @returns {string} - The effective role name for permission checking
 */
export const getEffectiveRole = (roles) => {
  const normalizedRoles = normalizeRoles(roles);
  
  // Check roles in priority order
  if (normalizedRoles.Admin) return 'Admin';
  if (normalizedRoles.Scheduling) return 'Scheduling';
  if (normalizedRoles.EngineerManager) return 'EngineerManager';
  if (normalizedRoles.Accounting) return 'Accounting';
  
  // Default to ReadOnly for everyone else
  return 'ReadOnly';
};

/**
 * Check if a user can edit a specific field
 * 
 * @param {Object} roles - Object containing all user roles
 * @param {string} fieldName - The name of the field to check
 * @param {Object} job - The job object (used for conditional checks)
 * @returns {boolean} - Whether the user can edit this field
 */
export const canEditField = (roles, fieldName, job = {}) => {
  const effectiveRole = getEffectiveRole(roles);
  const permissions = FIELD_PERMISSIONS[effectiveRole];
  
  if (!permissions) {
    return false; // Unknown role, deny access
  }
  
  // Admin and roles with canEditAnyField = true can edit everything
  if (permissions.canEditAnyField) {
    // Check for restricted fields with conditions (like Scheduling role)
    if (permissions.restrictedFields && permissions.restrictedFields.includes(fieldName)) {
      if (permissions.restrictedFieldsCondition && permissions.restrictedFieldsCondition(job)) {
        return false; // Field is restricted under current conditions
      }
    }
    return true;
  }
  
  // For other roles, check if the field is in their allowed list
  if (Array.isArray(permissions.editableFields)) {
    return permissions.editableFields.includes(fieldName);
  }
  
  return false;
};

/**
 * Check if a user can edit any field (general edit permission)
 * This is used for broader permission checks like showing edit buttons
 * 
 * @param {Object} roles - Object containing all user roles
 * @returns {boolean} - Whether the user has any edit permissions
 */
export const hasAnyEditPermission = (roles) => {
  const effectiveRole = getEffectiveRole(roles);
  const permissions = FIELD_PERMISSIONS[effectiveRole];
  
  if (!permissions) {
    return false;
  }
  
  // If they can edit any field or have specific editable fields
  return permissions.canEditAnyField || 
         (Array.isArray(permissions.editableFields) && permissions.editableFields.length > 0);
};

/**
 * Get all editable fields for a user role
 * 
 * @param {Object} roles - Object containing all user roles
 * @returns {Array|string} - Array of field names or 'all' for unrestricted roles
 */
export const getEditableFields = (roles) => {
  const effectiveRole = getEffectiveRole(roles);
  const permissions = FIELD_PERMISSIONS[effectiveRole];
  
  if (!permissions) {
    return [];
  }
  
  if (permissions.canEditAnyField) {
    return 'all';
  }
  
  return permissions.editableFields || [];
};

/**
 * Enhanced permission check that considers both canEdit prop and role-based permissions
 * This replaces the current hasEditAccess pattern used throughout the components
 * 
 * @param {boolean} canEdit - The canEdit prop passed from parent
 * @param {Object} roles - Object containing all user roles
 * @param {string} fieldName - Optional field name for field-specific checks
 * @param {Object} job - Optional job object for conditional checks
 * @returns {boolean} - Whether editing is allowed
 */
export const hasEditAccess = (canEdit, roles, fieldName = null, job = {}) => {
  // If canEdit prop is false, deny all editing regardless of role
  if (!canEdit) {
    return false;
  }
  
  // If no specific field is being checked, use general edit permission
  if (!fieldName) {
    return hasAnyEditPermission(roles);
  }
  
  // For specific field checks, use field-level permissions
  return canEditField(roles, fieldName, job);
};

/**
 * Check if user can view action columns (Admin/Scheduling/EngineerManager only for certain tables)
 * 
 * @param {Object} roles - Object containing all user roles
 * @returns {boolean} - Whether to show action columns
 */
export const canViewActionColumn = (roles) => {
  return roles.Admin || roles.Scheduling || roles.EngineerManager || roles.engineermanager;
};

/**
 * Check if user can create new jobs
 * 
 * @param {Object} roles - Object containing all user roles
 * @returns {boolean} - Whether user can create new jobs
 */
export const canCreateJobs = (roles) => {
  return roles.Admin || roles.Scheduling;
};

/**
 * Check if user can export/print schedules
 * 
 * @param {Object} roles - Object containing all user roles
 * @returns {boolean} - Whether user can export/print
 */
export const canExportSchedules = (roles) => {
  // Most roles can export/print for their own viewing
  return hasAnyEditPermission(roles) || roles.ReadOnly;
};

/**
 * Get permission summary for debugging
 * 
 * @param {Object} roles - Object containing all user roles
 * @returns {Object} - Permission summary for debugging
 */
export const getPermissionSummary = (roles) => {
  const effectiveRole = getEffectiveRole(roles);
  
  return {
    effectiveRole,
    hasAnyEditPermission: hasAnyEditPermission(roles),
    canViewActionColumn: canViewActionColumn(roles),
    canCreateJobs: canCreateJobs(roles),
    canExportSchedules: canExportSchedules(roles),
    editableFields: getEditableFields(roles),
    allRoles: Object.keys(roles).filter(role => roles[role])
  };
};

/**
 * Centralized helper to determine if a field is editable for the current user/role/job.
 * This should be used everywhere in the UI for editability checks.
 * 
 * @param {Object} roles - User roles (object or array)
 * @param {string} field - Field name
 * @param {Object} job - Job object (optional)
 * @returns {boolean}
 */
export const isFieldEditable = (roles, field, job = {}) => {
  // Always use normalized roles for consistency
  const normalizedRoles = normalizeRoles(roles);

  // EngineerManager restriction: can only edit engineering fields unless also Admin or Scheduling
  if (
    (normalizedRoles.engineermanager || normalizedRoles.EngineerManager) &&
    !normalizedRoles.Admin &&
    !normalizedRoles.Scheduling
  ) {
    const engineeringFields = [
      'MechanicalEngineer',
      'ElectricalEngineer',
      'ControlsEngineer',
      'MechanicalDue',
      'MechanicalDone',
      'MechanicalDuration',
      'ElectricalDue',
      'ElectricalDone',
      'ElectricalDuration',
      'ControlsDue',
      'ControlsDone',
      'ControlsDuration'
    ];
    if (!engineeringFields.includes(field)) {
      return false;
    }
  }

  // Fallback to standard field permission logic
  return canEditField(roles, field, job);
};

// Default export
const schedulePermissions = {
  getEffectiveRole,
  canEditField,
  hasAnyEditPermission,
  getEditableFields,
  hasEditAccess,
  canViewActionColumn,
  canCreateJobs,
  canExportSchedules,
  getPermissionSummary,
  isFieldEditable
};

export default schedulePermissions;
